/**
 * UserAvatar Component
 * Displays user profile photo with initials fallback
 * Follows InvestorAvatar pattern for consistency
 */

import React, { useState, useEffect } from 'react';
import { UserIcon } from '@heroicons/react/24/outline';
import { getUserAvatarColor } from '../../utils/userAvatarColor';

interface UserAvatarProps {
  user: {
    displayName?: string;
    email?: string;
    photoURL?: string;
  };
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  showBorder?: boolean;
  /** Rounded rectangle instead of circle (e.g. nav bar, no pill wrapper) */
  softCorners?: boolean;
  /** When set, initials use the hashed notes palette. Nav stays navy if omitted. */
  colorSeed?: string;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ 
  user, 
  size = 'md',
  className = '',
  showBorder = true,
  softCorners = false,
  colorSeed,
}) => {
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(true);
  const photoURL = (user as any)?.photoURL;

  // Reset states when photoURL changes
  useEffect(() => {
    setImageError(false);
    setLoading(true);
  }, [photoURL]);

  const sizeClasses = {
    xs: 'w-6 h-6 text-[10px]',
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-16 h-16 text-lg'
  };
  
  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8'
  };

  // Generate initials from display name or email
  const getInitials = (): string => {
    if (user?.displayName) {
      const parts = user.displayName.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return user.displayName[0].toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return '';
  };

  const initials = getInitials();
  const borderClass = showBorder ? 'ring-2 ring-white shadow-sm' : '';
  const cornerClass = softCorners ? 'rounded-lg' : 'rounded-full';
  const hasValidPhoto = photoURL && !imageError;
  const seed = colorSeed?.trim();
  const washClass = seed ? getUserAvatarColor(seed) : 'bg-primary-500/10 text-primary-500';

  // Render photo if available
  if (hasValidPhoto) {
    return (
      <div className={`${sizeClasses[size]} ${cornerClass} overflow-hidden ${borderClass} ${className} relative bg-gray-50`}>
        {/* Loading Skeleton */}
        {loading && (
          <div className="absolute inset-0 bg-primary-500/5 animate-pulse" />
        )}
        
        <img 
          src={photoURL} 
          alt={user?.displayName || 'User'}
          className={`w-full h-full object-cover transition-opacity duration-300 ${loading ? 'opacity-0' : 'opacity-100'}`}
          onError={() => { setImageError(true); setLoading(false); }}
          onLoad={() => setLoading(false)}
          referrerPolicy="no-referrer"
        />
      </div>
    );
  }

  // Render initials fallback
  return (
    <div 
      className={`${sizeClasses[size]} ${cornerClass} ${washClass} flex items-center justify-center font-medium ${borderClass} ${className}`}
    >
      {initials || <UserIcon className={iconSizes[size]} />}
    </div>
  );
};

export default UserAvatar;
